import { Dialog, Grid, Table, TableBody, TableHead, TableRow, Typography } from "@mui/material";
import React, { useState, memo } from "react";

import { useTranslation } from "react-i18next";
import DataActions from "./DataActions/DataActions";
import { Props } from "./interfaces";
import { Container, DialogContent, StyledTableCell, TableContainer, TextMessage } from "./styles";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { SKILLS_CATEGORIES } from "../../../../utils/constants/constants";
import { intlFormatDateShort } from "../../../../utils/time-utils";
import { AddEditSkills } from "../AddEditSkills/AddEditSkills";

const SkillsDetailsModal = ({ children, skillCategory, notifyParent, candidate }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.candidate_skills";

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const currSkills =
    candidate?.skills?.length !== 0
      ? candidate?.skills.filter((currSkill) => {
          if (currSkill.skill && currSkill.skill.category) return currSkill.skill.category.id === skillCategory.id;
        })
      : [];

  const getWidth = (fieldTitle: string): number => {
    const field = columns.find((obj) => obj.field === fieldTitle);

    if (field) return field.width;

    return 0;
  };

  const columns = [
    {
      title: t(`${tRP}.fields.skills`),
      field: "skill",
      width: 25,
    },
    {
      title: t(`${tRP}.fields.skill_level`),
      field: "skillLevel",
      width: 25,
    },
    {
      title: t(`${tRP}.fields.date`),
      field: "date",
      width: 25,
    },
    {
      title: t(`${tRP}.fields.years_of_experience`),
      field: "yearsOfExperience",
      width: 25,
    },
    {
      title: "",
      field: "actions",
      width: 0,
    },
  ];

  return (
    <>
      <Grid
        sx={{ display: "flex", alignItems: "center" }}
        onClick={() => setOpen(true)}
      >
        {children}
      </Grid>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          title={`${skillCategory.name} skills`}
          handleClose={() => setOpen(false)}
        />

        <LoadingOverlay loading={loading}>
          <DialogContent>
            {currSkills?.length === 0 ? (
              <TextMessage>There are no skills</TextMessage>
            ) : (
              <>
                <Container
                  style={{
                    width: "100%",
                    minHeight: "200px",
                    maxHeight: "600px",
                    marginBottom: "20px",
                  }}
                >
                  <TableContainer>
                    {
                      <Table
                        size={"small"}
                        stickyHeader={true}
                      >
                        <TableHead>
                          <TableRow>
                            {columns.map((column, idx) => (
                              <StyledTableCell
                                key={idx}
                                align="center"
                                width={column.width}
                              >
                                <Typography>{column.title}</Typography>
                              </StyledTableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {currSkills.map((item, idx) => (
                            <TableRow key={idx}>
                              <StyledTableCell
                                width={getWidth("skill")}
                                align="center"
                              >
                                {item?.skill?.name && <Typography>{item.skill.name}</Typography>}
                              </StyledTableCell>

                              <StyledTableCell
                                width={getWidth("skillLevel")}
                                align="center"
                              >
                                {item?.skillLevel?.name && item?.skillLevel?.altName && (
                                  <Typography>
                                    {skillCategory.id === SKILLS_CATEGORIES.language.id
                                      ? item.skillLevel.altName
                                      : item.skillLevel.name}
                                  </Typography>
                                )}
                              </StyledTableCell>

                              <StyledTableCell
                                width={getWidth("date")}
                                align="center"
                              >
                                {item?.date && <Typography>{intlFormatDateShort(new Date(item.date))}</Typography>}
                              </StyledTableCell>

                              <StyledTableCell
                                width={getWidth("yearsOfExperience")}
                                align="center"
                              >
                                <Typography>{item.yearsOfExperience}</Typography>
                              </StyledTableCell>

                              <StyledTableCell
                                width={getWidth("actions")}
                                actionsWidth={100}
                                align="center"
                              >
                                <DataActions>
                                  <AddEditSkills
                                    effect={"edit"}
                                    item={item}
                                    handleNotifyParent={notifyParent}
                                    candidate={candidate}
                                    skillCategory={skillCategory}
                                    buttonVariant={"text"}
                                  />
                                </DataActions>
                              </StyledTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    }
                  </TableContainer>
                </Container>
              </>
            )}
          </DialogContent>
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default memo(SkillsDetailsModal);
